import { supabase } from '../lib/supabase';

const messagingService = {
  /**
   * Get all conversations for the current user
   */
  async getConversations() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return { data: [], error: null };

      const { data, error } = await supabase?.from('conversations')?.select(`
          *,
          conversation_participants(id, user_id, participant_name, participant_email, participant_role, last_read_at),
          messages(id, content, sender_name, created_at, message_type)
        `)?.or(`created_by.eq.${user?.id}`)?.order('last_message_at', { ascending: false });

      if (error) throw error;

      // Also get conversations where user is a participant
      const { data: participantConvs, error: partError } = await supabase?.from('conversation_participants')?.select(`
          conversation_id,
          conversations(
            *,
            conversation_participants(id, user_id, participant_name, participant_email, participant_role, last_read_at),
            messages(id, content, sender_name, created_at, message_type)
          )
        `)?.eq('user_id', user?.id);

      if (partError) throw partError;

      const participantConvList = participantConvs
        ?.map(p => p?.conversations)
        ?.filter(Boolean) || [];

      // Merge and deduplicate
      const allConvs = [...(data || [])];
      participantConvList?.forEach(pc => {
        if (!allConvs?.find(c => c?.id === pc?.id)) {
          allConvs?.push(pc);
        }
      });

      // Sort by last_message_at
      allConvs?.sort((a, b) => new Date(b?.last_message_at) - new Date(a?.last_message_at));

      return { data: allConvs, error: null };
    } catch (error) {
      console.error('Error fetching conversations:', error);
      return { data: [], error: error?.message };
    }
  },

  /**
   * Get a single conversation with messages
   */
  async getConversation(conversationId) {
    try {
      const { data, error } = await supabase?.from('conversations')?.select(`
          *,
          conversation_participants(id, user_id, participant_name, participant_email, participant_role, last_read_at)
        `)?.eq('id', conversationId)?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching conversation:', error);
      return { data: null, error: error?.message };
    }
  },

  /**
   * Get messages for a conversation
   */
  async getMessages(conversationId) {
    try {
      const { data, error } = await supabase?.from('messages')?.select(`
          *,
          message_attachments(id, file_name, file_path, file_size, file_type)
        `)?.eq('conversation_id', conversationId)?.order('created_at', { ascending: true });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching messages:', error);
      return { data: [], error: error?.message };
    }
  },

  /**
   * Create a new conversation linked to a booking
   */
  async createConversation({ bookingId, subject, participantName, participantEmail }) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return { data: null, error: 'Non authentifié' };

      const { data: conv, error: convError } = await supabase?.from('conversations')?.insert({
          booking_id: bookingId || null,
          subject: subject || 'Nouvelle conversation',
          created_by: user?.id,
          last_message_at: new Date()?.toISOString(),
        })?.select()?.single();

      if (convError) throw convError;

      // Add creator as participant
      await supabase?.from('conversation_participants')?.insert({
        conversation_id: conv?.id,
        user_id: user?.id,
        participant_name: user?.user_metadata?.full_name || user?.email,
        participant_email: user?.email,
        participant_role: 'client',
      });

      return { data: conv, error: null };
    } catch (error) {
      console.error('Error creating conversation:', error);
      return { data: null, error: error?.message };
    }
  },

  /**
   * Send a message in a conversation
   */
  async sendMessage({ conversationId, content, messageType = 'text' }) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return { data: null, error: 'Non authentifié' };

      const { data, error } = await supabase?.from('messages')?.insert({
          conversation_id: conversationId,
          sender_id: user?.id,
          sender_name: user?.user_metadata?.full_name || user?.email || 'Utilisateur',
          sender_email: user?.email,
          content,
          message_type: messageType,
          is_read: false,
        })?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error sending message:', error);
      return { data: null, error: error?.message };
    }
  },

  /**
   * Upload a file attachment for a message
   */
  async uploadAttachment(file, userId) {
    try {
      const timestamp = Date.now();
      const sanitizedName = file?.name?.replace(/[^a-zA-Z0-9._-]/g, '_');
      const filePath = `${userId}/${timestamp}-${sanitizedName}`;

      const { data, error } = await supabase?.storage?.from('message-attachments')?.upload(filePath, file, { cacheControl: '3600', upsert: false });

      if (error) throw error;
      return { data: { path: filePath, ...data }, error: null };
    } catch (error) {
      console.error('Error uploading attachment:', error);
      return { data: null, error: error?.message };
    }
  },

  /**
   * Get signed URL for a private attachment
   */
  async getAttachmentUrl(filePath) {
    try {
      const { data, error } = await supabase?.storage?.from('message-attachments')?.createSignedUrl(filePath, 3600);

      if (error) throw error;
      return { url: data?.signedUrl, error: null };
    } catch (error) {
      console.error('Error getting attachment URL:', error);
      return { url: null, error: error?.message };
    }
  },

  /**
   * Save attachment record after upload
   */
  async saveAttachment({ messageId, fileName, filePath, fileSize, fileType }) {
    try {
      const { data, error } = await supabase?.from('message_attachments')?.insert({
          message_id: messageId,
          file_name: fileName,
          file_path: filePath,
          file_size: fileSize,
          file_type: fileType,
        })?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error saving attachment:', error);
      return { data: null, error: error?.message };
    }
  },

  /**
   * Mark messages as read
   */
  async markAsRead(conversationId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return;

      await supabase?.from('messages')?.update({ is_read: true })?.eq('conversation_id', conversationId)?.neq('sender_id', user?.id);

      await supabase?.from('conversation_participants')?.update({ last_read_at: new Date()?.toISOString() })?.eq('conversation_id', conversationId)?.eq('user_id', user?.id);
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  },

  /**
   * Get unread message count for current user
   */
  async getUnreadCount() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return { count: 0, error: null };

      const { data: participations } = await supabase?.from('conversation_participants')?.select('conversation_id')?.eq('user_id', user?.id);

      if (!participations?.length) return { count: 0, error: null };

      const convIds = participations?.map(p => p?.conversation_id);

      const { count, error } = await supabase?.from('messages')?.select('*', { count: 'exact', head: true })?.in('conversation_id', convIds)?.neq('sender_id', user?.id)?.eq('is_read', false);

      if (error) throw error;
      return { count: count || 0, error: null };
    } catch (error) {
      console.error('Error getting unread count:', error);
      return { count: 0, error: error?.message };
    }
  },

  /**
   * Subscribe to new messages in a conversation
   */
  subscribeToMessages(conversationId, onMessage) {
    const channel = supabase?.channel(`messages:${conversationId}:${Date.now()}`)?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`,
        },
        (payload) => {
          onMessage(payload?.new);
        }
      )?.subscribe();

    return channel;
  },

  /**
   * Unsubscribe from a channel
   */
  unsubscribe(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  },
};

export default messagingService;
